/**
 * get a number in the range [start, end]
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random
 */
export const randomInteger = (end: number, start = 0) => {
  if (end < start) return 0;
  const min = Math.ceil(start);
  const max = Math.floor(end);
  return min + Math.floor(Math.random() * (max - min + 1));
};

/**
 * Returns a random entry in A
 */
export const randomEntry = <T>(A: T[]): T => {
  const index = randomInteger(A.length - 1);
  return A[index];
};

/**
 * Modified Fisher-Yates shuffle for picking k elements from an array
 * @link https://en.wikipedia.org/wiki/Reservoir_sampling#Relation_to_Fisher%E2%80%93Yates_shuffle
 * @param {Array} A an array
 * @param {number} k how many to pick
 * @return {Array} B such that B is a subset of A with k elements
 */
export const sample = <T>(A: T[] = [], k = 0) => {
  const B = A.slice(0, k);
  for (let i = k; i < A.length; i += 1) {
    const j = randomInteger(i);
    if (j < k) {
      B[j] = A[i];
    }
  }
  return B;
};

/**
 * Fisher-Yates shuffle
 * @link https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle
 * @link https://blog.codinghorror.com/the-danger-of-naivete/
 * @param {Array} A an array
 * @return {Array} a subset of A
 */
export const shuffle = <T>(A: T[] = []) => {
  const B: T[] = [];
  for (let i = 0; i < A.length; i += 1) {
    const j = randomInteger(i);
    if (j !== i) {
      B[i] = B[j];
    }
    B[j] = A[i];
  }
  return B;
};

/**
 * @param {Array} A an array
 * @return {Array} B such that B is a subset of A
 */
export const randomSubArray = <T>(A: T[] = []) => {
  const B: T[] = [];
  for (let index = 0; index < A.length; index += 1) {
    const element = A[index];
    if (Math.random() > 0.5) {
      B.push({ ...element });
    }
  }
  return B;
};

export default { sample, randomInteger, shuffle, randomSubArray };
